export type ContentAssistTarget = 'email_subject' | 'email_html' | 'email_text' | 'landing_html';
export type ContentAssistAction = 'generate' | 'rewrite' | 'improve' | 'shorten';
export type ContentAssistTone = '' | 'professional' | 'urgent' | 'neutral' | 'friendly';

export interface ContentAssistMetadata {
  name?: string;
  subject?: string;
  category?: string;
  difficulty?: string;
  attack_vector?: string;
  impersonated_brand?: string;
}

export interface ContentAssistInput {
  target: ContentAssistTarget;
  action: ContentAssistAction;
  tone?: ContentAssistTone;
  instruction?: string;
  selected_text?: string;
  before_text?: string;
  after_text?: string;
  document_html?: string;
  document_text?: string;
  metadata?: ContentAssistMetadata;
}

export interface ContentAssistResponse {
  target: ContentAssistTarget;
  replacement: string;
}

export interface AssistSnapshot {
  target: ContentAssistTarget;
  label: string;
  scope: 'selection' | 'field';
  selectedText: string;
  beforeText: string;
  afterText: string;
  documentHtml: string;
  documentText: string;
  fieldValue: string;
  selectionStart: number | null;
  selectionEnd: number | null;
  range?: Range | null;
  metadata?: ContentAssistMetadata;
}

const CONTEXT_LIMIT = 4000;

export function captureTextSelection(value: string, start: number | null, end: number | null) {
  const selected = start != null && end != null && start >= 0 && end <= value.length && start < end;
  if (!selected || start == null || end == null) {
    return {
      scope: 'field' as const,
      selectedText: '',
      beforeText: '',
      afterText: '',
      selectionStart: null,
      selectionEnd: null,
    };
  }
  return {
    scope: 'selection' as const,
    selectedText: value.slice(start, end),
    beforeText: value.slice(Math.max(0, start - CONTEXT_LIMIT), start),
    afterText: value.slice(end, Math.min(value.length, end + CONTEXT_LIMIT)),
    selectionStart: start,
    selectionEnd: end,
  };
}

export function replaceText(value: string, start: number | null, end: number | null, replacement: string): string {
  if (start == null || end == null || start === end || start < 0 || end > value.length || start > end) return replacement;
  return value.slice(0, start) + replacement + value.slice(end);
}

export function captureDocumentSelection(doc: Document): { selectedText: string; range: Range } | null {
  const selection = doc.getSelection();
  if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return null;
  const range = selection.getRangeAt(0).cloneRange();
  const selectedText = selection.toString();
  if (!selectedText) return null;
  return { selectedText, range };
}

export function applyDocumentSelection(doc: Document, range: Range | null, replacement: string): string {
  if (!range) return replacement;
  range.deleteContents();
  range.insertNode(doc.createTextNode(replacement));
  doc.getSelection()?.removeAllRanges();
  return doc.body.innerHTML;
}

export function describeAssistFailure(error: unknown): { message: string; unconfigured: boolean } {
  const response = (error as { response?: { status?: number; data?: { message?: string } } } | null)?.response;
  const message = response?.data?.message || 'AI content assistance failed';
  return {
    message,
    unconfigured: response?.status === 503 || /not configured/i.test(message),
  };
}
